import type { Permission } from './api'

// Shared helpers for translating the flat permission catalog into the
// GitHub-PAT-style "No access / Read / Read & write" model used by both the
// editable RolePermissionPicker and the read-only role detail matrix.

export type Level = 'none' | 'read' | 'readwrite'

export interface CategoryGroup {
  category: string
  readKey: string
  writeKey: string
  sensitive: boolean
  /** Short summary shown inline (the read key's description, or the first key). */
  description: string
  /** Full read-key description, surfaced in the info tooltip. */
  readDescription: string
  /** Full write-key description, surfaced in the info tooltip (if a write key exists). */
  writeDescription: string
}

export const LEVEL_LABELS: Record<Level, string> = {
  none: 'No access',
  read: 'Read',
  readwrite: 'Read & write',
}

// Each resource category gets a No access / Read / Read & write segmented
// control. "write" implies "read". (Impersonation's write-equivalent key is
// impersonation.use, not .write.)
export function groupCatalog(catalog: Permission[]): CategoryGroup[] {
  const order: string[] = []
  const byCat: Record<string, Permission[]> = {}
  for (const perm of catalog) {
    if (!byCat[perm.category]) {
      byCat[perm.category] = []
      order.push(perm.category)
    }
    byCat[perm.category].push(perm)
  }
  return order.map((category) => {
    const perms = byCat[category]
    const readPerm = perms.find((p) => p.key.endsWith('.read'))
    const writePerm = perms.find((p) => !p.key.endsWith('.read'))
    return {
      category,
      readKey: readPerm?.key ?? '',
      writeKey: writePerm?.key ?? '',
      sensitive: perms.some((p) => p.sensitive),
      description: (readPerm ?? perms[0])?.description ?? '',
      readDescription: readPerm?.description ?? '',
      writeDescription: writePerm?.description ?? '',
    }
  })
}

export function levelFor(group: CategoryGroup, value: string[]): Level {
  const hasRead = group.readKey ? value.includes(group.readKey) : false
  const hasWrite = group.writeKey ? value.includes(group.writeKey) : false
  if (hasWrite) return 'readwrite'
  if (hasRead) return 'read'
  return 'none'
}
